import { useState, useMemo, useCallback } from "react";
import { cn } from "@/lib/utils";
import { IAirdropTaskRender } from "./task-renderer.types";
import { getAirdropArgumentValue } from "@/lib/challenges";
import { ChallengeCampaignEventState } from "@/types";
import { QuestTaskButton } from "../quest-task-button";

const QuizOption = ({
  value,
  label,
  isSelected,
  isDisabled,
  onClick,
}: {
  value: string;
  label: string;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isDisabled}
    className={cn(
      "relative w-full p-4 text-left transition-all duration-200 ease-out border-2 rounded-xl",
      "bg-white hover:bg-swapper-primary/5",
      isSelected ? "border-swapper-primary shadow-md" : "border-gray-200",
      isDisabled && "opacity-50 cursor-not-allowed hover:bg-white"
    )}
  >
    <span className={cn(
      "text-sm font-medium",
      isSelected ? "text-swapper-primary" : "text-swapper-secondary"
    )}>
      {label}
    </span>
  </button>
);

export const QuizRenderer = ({
  task,
  completeTaskMutation,
  campaign,
  address,
  overrideQuestion,
}: IAirdropTaskRender) => {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [input, setInput] = useState<string>("");

  const campaignTask = campaign.tasks?.find(
    (campaignTask) => campaignTask.id === task.taskGuid
  );

  const options = useMemo(() => {
    return JSON.parse(
      getAirdropArgumentValue(campaignTask?.arguments ?? [], "options") ?? "[]"
    ) as string[];
  }, [campaignTask?.arguments]);

  const randomizedOptions = useMemo(
    () => [...options].sort(() => Math.random() - 0.5),
    [options]
  );

  const isMultipleChoice = useMemo(
    () =>
      (getAirdropArgumentValue(campaignTask?.arguments ?? [], "options") ?? [])
        .length > 1,
    [campaignTask?.arguments]
  );

  const isDisabled = task.status === ChallengeCampaignEventState.SUCCESSFUL;
  const question = useMemo(
    () => overrideQuestion || getAirdropArgumentValue(campaignTask?.arguments ?? [], "question"),
    [campaignTask?.arguments, overrideQuestion]
  );

  const onPressHandler = useCallback(() => {
    const answer = isMultipleChoice ? selectedOption : input;
    completeTaskMutation.mutateAsync({
      campaignId: campaign.id,
      groupId: campaign.campaignGroup,
      taskName: task.taskName,
      extraArguments: [answer],
      taskGuid: task.taskGuid,
    });
  }, [
    completeTaskMutation,
    campaign,
    task,
    address,
    isMultipleChoice,
    selectedOption,
    input,
  ]);

  return (
    <div className="flex flex-col gap-6">
      {question && (
        <div className="text-lg font-bold text-swapper-secondary">
          {question}
        </div>
      )}

      {isMultipleChoice ? (
        <div className="flex flex-col gap-3">
          {randomizedOptions.map((option) => (
            <QuizOption
              key={option}
              value={option}
              label={option}
              isSelected={selectedOption === option}
              isDisabled={isDisabled}
              onClick={() => setSelectedOption(option)}
            />
          ))}
        </div>
      ) : (
        <div className="relative bg-white border-2 border-gray-200 rounded-xl p-4">
          <input
            disabled={isDisabled}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter the right answer"
            className="w-full bg-transparent border-none outline-none text-swapper-secondary placeholder:text-swapper-secondary/50 text-sm disabled:opacity-50"
          />
        </div>
      )}

      <QuestTaskButton
        buttonText="Submit Answer"
        taskMutation={completeTaskMutation}
        task={task}
        onClick={onPressHandler}
      />
    </div>
  );
};
